import type { BuildingGraphVolumeNode } from "building-types/building/buildingGraphTypes";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { PropertyPath } from "building-types/buildingGraphFilter/propertyCatalog";
import { RoomClass, RoomEnclosure, RoomType } from "building-types/rooms/roomTypesEnum";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { enumValues } from "common/utils/enum";
import {
  volumeNodeGetFaceNodeIds,
  volumeNodeGetFloorId,
  volumeNodeGetLevelId,
  volumeNodeGetPlaneHashes,
  volumeNodeGetRoofId,
  volumeNodeGetRoomEdgeHandles,
  volumeNodeGetRoomId,
  volumeNodeGetRoomType,
  volumeNodeGetSourceRoomId,
  volumeNodeGetTopologyFaceIds,
  volumeNodeGetWallPlaneHashes,
  volumeNodeParentHandleGetRoomClass,
  volumeNodeParentHandleGetRoomEnclosure,
  volumeNodeGetMembershipParentHandles,
} from "../entityFilterSelectors";
import type { EntityFilterSource, PropertyDescriptorForPath, PropertyValue } from "../entityFilterSourceTypes";

const VOLUME_NODE_DESCRIPTORS: { [P in PropertyPath<"VOLUME_NODE">]: PropertyDescriptorForPath<"VOLUME_NODE", P> } = {
  volumeNodeId: { kind: "SCALAR", type: "id" },
  buildingPlanarGraphId: { kind: "SCALAR", type: "string" },
  buildingPlanarGraphFaceIndex: { kind: "SCALAR", type: "number" },
  planeHashes: { kind: "SET", type: "string" },
  roomHandle: { kind: "SCALAR", type: "roomHandle" },
  sourceRoomHandle: { kind: "SCALAR", type: "roomHandle" },
  levelHandle: { kind: "SCALAR", type: "levelHandle" },
  floorHandle: { kind: "SCALAR", type: "floorHandle" },
  roofHandle: { kind: "SCALAR", type: "roofHandle" },
  roomClass: { kind: "SCALAR", type: "enum", values: enumValues(RoomClass) },
  roomEnclosure: { kind: "SCALAR", type: "enum", values: enumValues(RoomEnclosure) },
  roomType: { kind: "SCALAR", type: "enum", values: enumValues(RoomType) },
  roomEdgeHandles: { kind: "SET", type: "roomEdgeHandle" },
  faceNodeIds: { kind: "SET", type: "id" },
  wallPlaneHashes: { kind: "SET", type: "string" },
  topologyFaceIds: { kind: "SET", type: "id" },
};

function readVolumeNodeProperty(
  volumeNode: BuildingGraphVolumeNode,
  path: PropertyPath<"VOLUME_NODE">,
  building: BuildingGraphFilterBuilding,
): PropertyValue {
  switch (path) {
    case "volumeNodeId":
      return { kind: "SCALAR", value: volumeNode.volumeNodeId };
    case "buildingPlanarGraphId":
      return { kind: "SCALAR", value: volumeNode.buildingPlanarGraphId };
    case "buildingPlanarGraphFaceIndex":
      return { kind: "SCALAR", value: volumeNode.buildingPlanarGraphFaceIndex };
    case "planeHashes":
      return { kind: "SET", values: volumeNodeGetPlaneHashes(volumeNode, building) };
    case "roomHandle": {
      const roomId = volumeNodeGetRoomId(volumeNode, building);
      return { kind: "SCALAR", value: roomId ? { type: "ROOM", id: roomId } : undefined };
    }
    case "sourceRoomHandle": {
      const sourceRoomId = volumeNodeGetSourceRoomId(volumeNode, building);
      return { kind: "SCALAR", value: sourceRoomId ? { type: "ROOM", id: sourceRoomId } : undefined };
    }
    case "levelHandle": {
      const levelId = volumeNodeGetLevelId(volumeNode, building);
      return { kind: "SCALAR", value: levelId ? { type: "LEVEL", id: levelId } : undefined };
    }
    case "floorHandle": {
      const floorId = volumeNodeGetFloorId(volumeNode, building);
      return { kind: "SCALAR", value: floorId ? { type: "FLOOR", id: floorId } : undefined };
    }
    case "roofHandle": {
      const roofId = volumeNodeGetRoofId(volumeNode, building);
      return { kind: "SCALAR", value: roofId ? { type: "ROOF", id: roofId } : undefined };
    }
    case "roomClass": {
      const roomParent = volumeNodeGetMembershipParentHandles(volumeNode, building.buildingGraph).find(
        (handle) => handle.type === "ROOM",
      );
      return { kind: "SCALAR", value: volumeNodeParentHandleGetRoomClass(roomParent, building) };
    }
    case "roomEnclosure": {
      const roomParent = volumeNodeGetMembershipParentHandles(volumeNode, building.buildingGraph).find(
        (handle) => handle.type === "ROOM",
      );
      return { kind: "SCALAR", value: volumeNodeParentHandleGetRoomEnclosure(roomParent, building) };
    }
    case "roomType":
      return { kind: "SCALAR", value: volumeNodeGetRoomType(volumeNode, building) };
    case "roomEdgeHandles":
      return { kind: "SET", values: volumeNodeGetRoomEdgeHandles(volumeNode, building) };
    case "faceNodeIds":
      return { kind: "SET", values: volumeNodeGetFaceNodeIds(volumeNode) };
    case "wallPlaneHashes":
      return { kind: "SET", values: volumeNodeGetWallPlaneHashes(volumeNode, building) };
    case "topologyFaceIds":
      return { kind: "SET", values: volumeNodeGetTopologyFaceIds(volumeNode, building) };
    default:
      return assertUnreachable(path);
  }
}

export const volumeNodeSource: EntityFilterSource<"VOLUME_NODE"> = {
  sourceId: "VOLUME_NODE",
  *iterateRows(building) {
    for (const volumeNodeId in building.buildingGraph.volumeNodes) {
      const volumeNode = building.buildingGraph.volumeNodes[volumeNodeId];
      if (volumeNode) {
        yield volumeNode;
      }
    }
  },
  identity(volumeNode) {
    return volumeNode.volumeNodeId;
  },
  descriptor(path) {
    return VOLUME_NODE_DESCRIPTORS[path];
  },
  readProperty(volumeNode, path, building) {
    return readVolumeNodeProperty(volumeNode, path, building);
  },
};
